import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  GraduationCap, BookmarkCheck, ShoppingBag, Award, Music, Crown, ArrowRight, Sparkles,
} from "lucide-react";
import { getMemberDashboard, type MemberDashboard } from "@/lib/membership.functions";
import { createBillingPortalSession } from "@/lib/shop.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { UpgradePrompt } from "@/components/site/UpgradePrompt";
import { track } from "@/lib/analytics";

function ManageMembershipButton() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div>
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setErr(null);
          track("membership_manage_click", { context: "dashboard" });
          try {
            const result = await createBillingPortalSession({
              data: { returnUrl: window.location.href, environment: getStripeEnvironment() },
            });
            if ("error" in result) { setErr(result.error); return; }
            window.location.href = result.url;
          } catch (e) {
            setErr(e instanceof Error ? e.message : "Could not open the billing portal.");
          } finally {
            setBusy(false);
          }
        }}
        className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian shadow-gold disabled:opacity-60"
      >
        {busy ? "Opening billing…" : "Manage membership"} <ArrowRight size={14} aria-hidden />
      </button>
      {err && <p className="mt-2 text-xs text-red-400" role="alert">{err}</p>}
    </div>
  );
}


export const Route = createFileRoute("/_authenticated/dashboard")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Member Dashboard · KAfrequency" },
      { name: "description", content: "Continue learning, saved activations, your library, Academy progress, certifications, and membership status." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DashboardPage,
});

function fmt(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
}

function DashboardPage() {
  const load = useServerFn(getMemberDashboard);
  const [d, setD] = useState<MemberDashboard | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    track("dashboard_view");
    load().then(setD).catch((e) => setErr(e?.message ?? "Could not load your dashboard."));
  }, [load]);

  if (err) return <div className="mx-auto max-w-5xl p-6 text-sm text-red-400" role="alert">{err}</div>;
  if (!d) return <div className="mx-auto max-w-5xl p-6 text-sm text-muted-foreground">Loading your dashboard…</div>;

  const m = d.membership;

  return (
    <div className="mx-auto max-w-6xl space-y-8 px-4 py-10 sm:px-6">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Member Dashboard</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl">
          Welcome back{d.displayName ? `, ${d.displayName}` : ""}.
        </h1>
      </header>

      {/* Membership status */}
      <section aria-labelledby="membership-heading" className="rounded-2xl border hairline bg-charcoal p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 id="membership-heading" className="flex items-center gap-2 font-serif text-2xl">
              <Crown size={18} className="text-gold" aria-hidden /> {m.active ? m.label : "Free Account"}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {m.active
                ? m.lifetime
                  ? "Lifetime access — no renewal required."
                  : m.cancelAtPeriodEnd
                    ? `Access continues until ${fmt(m.endsAt)}.`
                    : `Renews ${fmt(m.renewsAt)}${m.cadence ? ` · billed ${m.cadence}` : ""}.`
                : "You have a free account. Membership opens daily activations, the full Academy canon, and members-only music."}
            </p>
          </div>
          {m.active && !m.lifetime ? (
            <ManageMembershipButton />
          ) : (
            <Link
              to={m.active ? "/account" : "/membership"}
              onClick={() => track(m.active ? "membership_page_view" : "upgrade_prompt_click", { context: "dashboard" })}
              className="inline-flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian shadow-gold"
            >
              {m.active ? "Manage membership" : "Become a member"} <ArrowRight size={14} aria-hidden />
            </Link>
          )}

        </div>
      </section>

      {!m.active && <UpgradePrompt context="dashboard" />}

      {/* KA Intelligence — first-class product entry point */}
      <section aria-labelledby="ka-intelligence-heading" className="rounded-2xl border hairline bg-charcoal p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 id="ka-intelligence-heading" className="font-serif text-2xl">KA Intelligence</h2>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">
              Ask about the music, books, gates, and practice — answers grounded in the published
              canon, with sources shown.
            </p>
          </div>
          <Link
            to="/intelligence"
            search={{ from: undefined }}
            className="inline-flex items-center gap-2 rounded-full border border-gold px-5 py-2.5 text-sm font-medium text-gold-luminous transition-colors hover:bg-gold hover:text-obsidian"
          >
            Enter KA Intelligence <ArrowRight size={14} aria-hidden />
          </Link>
        </div>
      </section>

      {/* Continue learning */}
      <Panel icon={GraduationCap} title="Continue learning" href="/academy" hrefLabel="Academy">
        {d.continueLearning.length === 0 ? (
          <Empty text="No lessons in progress yet." cta={{ to: "/academy", label: "Start your first lesson" }} />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {d.continueLearning.map((l) => (
              <li key={l.lessonId} className="rounded-xl border hairline bg-black/30 p-4">
                <p className="font-serif text-lg">{l.title}</p>
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={Math.round(l.progressPct)} aria-valuemin={0} aria-valuemax={100} aria-label={`${l.title} progress`}>
                  <div className="h-full bg-gold-gradient" style={{ width: `${Math.min(100, Math.max(0, l.progressPct))}%` }} />
                </div>
                {l.domainSlug && (
                  <Link
                    to="/academy/$domain/lesson/$lesson"
                    params={{ domain: l.domainSlug, lesson: l.slug }}
                    onClick={() => track("dashboard_continue_learning", { lesson: l.slug })}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-gold underline underline-offset-4"
                  >
                    Resume lesson <ArrowRight size={12} aria-hidden />
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* Academy progress */}
      <section aria-label="Academy progress" className="grid gap-4 sm:grid-cols-3">
        <Stat label="Lessons started" value={d.academy.lessonsStarted} />
        <Stat label="Lessons completed" value={d.academy.lessonsCompleted} />
        <Stat label="Average mastery" value={`${d.academy.avgMastery}%`} />
      </section>

      {/* Saved activations */}
      <Panel icon={BookmarkCheck} title="Saved activations" href="/activations" hrefLabel="All activations">
        {d.savedActivations.length === 0 ? (
          <Empty text="Nothing saved yet." cta={{ to: "/activations", label: "Browse activations" }} />
        ) : (
          <ul className="flex flex-wrap gap-2">
            {d.savedActivations.map((a) => (
              <li key={a.slug}>
                <Link to="/activations/$slug" params={{ slug: a.slug }} className="inline-flex rounded-full border hairline px-4 py-2 text-xs hover:border-gold">
                  {a.slug.replace(/-/g, " ")}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* Library / purchases */}
      <Panel icon={ShoppingBag} title="Your library" href="/downloads" hrefLabel="Downloads">
        {d.purchases.length === 0 && d.orders.length === 0 ? (
          <Empty text="No purchases yet." cta={{ to: "/shop", label: "Visit the shop" }} />
        ) : (
          <ul className="space-y-2 text-sm">
            {d.purchases.map((p) => (
              <li key={p.productSlug} className="flex items-center justify-between rounded-lg border hairline bg-black/30 px-4 py-3">
                <span>{p.productSlug.replace(/-/g, " ")}</span>
                <span className="text-xs text-muted-foreground">Since {fmt(p.grantedAt)}</span>
              </li>
            ))}
            {d.orders.map((o) => (
              <li key={`${o.slug}-${o.createdAt}`} className="flex items-center justify-between rounded-lg border hairline bg-black/20 px-4 py-3">
                <span>{o.title}</span>
                <span className="text-xs text-muted-foreground">{o.status} · {fmt(o.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* Certifications */}
      <Panel icon={Award} title="Certifications" href="/journey/certificates" hrefLabel="All certificates">
        {d.certificates.length === 0 ? (
          <Empty text="No certifications issued yet." cta={{ to: "/academy", label: "Start a certification track" }} />
        ) : (
          <ul className="space-y-2 text-sm">
            {d.certificates.map((c) => (
              <li key={c.code} className="flex items-center justify-between rounded-lg border hairline bg-black/30 px-4 py-3">
                <span>{c.title}</span>
                <Link to="/verify/$code" params={{ code: c.code }} className="text-xs text-gold underline underline-offset-4">
                  Verify
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* Recent releases */}
      <Panel icon={Music} title="Recent releases" href="/music" hrefLabel="All music">
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {d.recentReleases.map((r) => (
            <li key={r.slug}>
              <Link
                to="/music/$slug"
                params={{ slug: r.slug }}
                onClick={() => track("music_interaction", { action: "dashboard_open", slug: r.slug })}
                className="block rounded-xl border hairline bg-black/30 p-3 hover:border-gold"
              >
                {r.coverUrl ? (
                  <img src={r.coverUrl} alt="" loading="lazy" className="aspect-square w-full rounded-lg object-cover" />
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center rounded-lg bg-white/5 text-gold"><Sparkles size={18} aria-hidden /></div>
                )}
                <p className="mt-2 truncate text-xs">{r.title}</p>
              </Link>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function Panel({
  icon: Icon, title, href, hrefLabel, children,
}: {
  icon: React.ComponentType<{ size?: number; className?: string; "aria-hidden"?: boolean }>;
  title: string; href: string; hrefLabel: string; children: React.ReactNode;
}) {
  return (
    <section aria-label={title} className="rounded-2xl border hairline bg-charcoal p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h2 className="flex items-center gap-2 font-serif text-2xl">
          <Icon size={18} className="text-gold" aria-hidden /> {title}
        </h2>
        <Link to={href} className="text-xs text-gold underline underline-offset-4">{hrefLabel}</Link>
      </div>
      {children}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border hairline bg-charcoal p-5">
      <p className="font-serif text-3xl text-gold-luminous">{value}</p>
      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
    </div>
  );
}

function Empty({ text, cta }: { text: string; cta: { to: string; label: string } }) {
  return (
    <div className="rounded-xl border border-dashed border-white/15 p-6 text-center">
      <p className="text-sm text-muted-foreground">{text}</p>
      <Link to={cta.to} className="mt-3 inline-flex items-center gap-1 text-xs text-gold underline underline-offset-4">
        {cta.label} <ArrowRight size={12} aria-hidden />
      </Link>
    </div>
  );
}
